import React, { useRef, useState, useEffect } from "react";
import "./HomePage1.css";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { useNavigate } from "react-router-dom";
import { STATIC_IMAGES } from "../../data/staticData";
import { fetchSettings } from "../../data/catalog";

const DEFAULT_HERO = './bgimGW.jpeg';
// Below this width the hero uses the separate mobile/portrait image (if one is set).
const MOBILE_HERO_QUERY = '(max-width: 768px)';

// Featured images -> the category page each should open
const FEATURED_IMAGES = [
  { src: './soulinacuptea1.jpeg', fallback: './new.jpeg', label: 'Soul in a Cup', route: '/category/chai', className: 'featuredImageCard--tall' },
  { src: './chaipecharchatea2.jpeg', fallback: './slider1.jpeg', label: 'Chai Pe Charcha', route: '/category/subscription boxes', className: 'featuredImageCard--short' },
  { src: './stickysoultea3.jpeg', fallback: './slider2.jpeg', label: 'Sticky Soul', route: '/category/chai', className: 'featuredImageCard--short' },
  { src: './discoverybox2.jpeg', fallback: './slider3.jpeg', label: 'Discovery Box', route: '/category/subscription boxes', className: 'featuredImageCard--tall' },
];

const HomePage1 = () => {
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const [heroImages, setHeroImages] = useState({ desktop: '', mobile: '' });
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_HERO_QUERY).matches
  );

  useEffect(() => {
    let active = true;
    fetchSettings().then((s) => {
      if (active && s) setHeroImages({ desktop: s.heroImage || '', mobile: s.heroImageMobile || '' });
    });
    return () => { active = false; };
  }, []);

  // Swap between the desktop and mobile hero as the viewport changes.
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_HERO_QUERY);
    const onChange = (e) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // On phones prefer the mobile image, then the desktop one, then the built-in default.
  const heroImage =
    (isMobile ? heroImages.mobile || heroImages.desktop : heroImages.desktop) || DEFAULT_HERO;

  const imageSet1 = STATIC_IMAGES.slider;

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });

  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.3]);
  const borderRadius = useTransform(scrollYProgress, [0, 1], [0, 150]);


  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#F1E6D2",
        position: "relative",
        zIndex: "10",
      }}
    >
      <motion.div
        className="homepage1"
        ref={containerRef}
        style={{
          borderBottomLeftRadius: borderRadius,
          borderBottomRightRadius: borderRadius,
        }}
      >
        <motion.img
          src={heroImage}
          onError={(e) => { e.target.src = DEFAULT_HERO; }}
          className="background-img"
          style={{
            scale,
            transformOrigin: "center center",
          }}
        />
        <motion.div
          className="background-overlay"
          style={{
            scale,
            transformOrigin: "center center",
          }}
        />
      </motion.div>

      {/* Ancient Wisdom ribbon between hero and photo grid */}
      <div className="ancientWisdomRibbon">
        <p className="ancientWisdomText">Where Ancient Wisdom Meets Modern Wellness</p>
      </div>

      {/* Featured product image section - each opens its category */}
      <div className="featuredImageSection">
        <div className="featuredImageGrid">
          {FEATURED_IMAGES.map((card) => (
            <div
              key={card.label}
              className={`featuredImageCard ${card.className}`}
              role="button"
              tabIndex={0}
              onClick={() => navigate(card.route)}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate(card.route); }}
            >
              <img src={card.src} alt={card.label} onError={(e) => { e.target.src = card.fallback; }} />
              <div className="featuredImageLabel">{card.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Product showcase section below hero */}
      <div className="heroProductSection">
        <div className="heroProductLeft">
          <p className="heroProductLabel">SIGNATURE BLEND</p>
          {/* The <br> only renders on mobile - see .heroProductTitleBr */}
          <h2 className="heroProductTitle">Crafted with ancient spices,<br className="heroProductTitleBr" /> brewed with love</h2>
          <p className="heroProductDesc">From cardamom to saffron, every ingredient is chosen to heal, nourish and connect you to something deeper.</p>
          <button className="heroProductBtn" onClick={() => window.location.href = '/allproduct'}>Shop Now →</button>
        </div>
        <div className="heroProductRight">
          <img src="./chaipecharchatea2.jpeg" alt="Elaichi & Love product" className="heroProductImg" onError={e => { e.target.src = './new.jpeg'; }} />
        </div>
      </div>

      <div className="modernRootsRibbon">
        <p className="modernRootsText">MODERN ROOTS. SOULFUL SIPS.</p>
        <p className="modernRootsBodyText">
          This isn't just chai. It's a story. A journey. A revolution in a cup. From my mother's kitchen in Mumbai to the streets of Glasgow - every cup carries generations of love, healing, and connection. Elaichi &amp; Love: wellness chai that heals, connects, and slows you down.
        </p>
      </div>


      <div className="sweetSlider">
        <div className="sweetSliderRibbon">
          <p className="sweetSliderHeadingText">
            {/* The <br> only renders on mobile - see .sweetSliderHeadingBr */}
            UK's first food revolution :<br className="sweetSliderHeadingBr" /> for the people, by the people
          </p>
        </div>
        <Marquee speed={100} gradient={false}>
          <div className="sweetSliderImageContainer">
            {imageSet1.map((image, index) => {
              return <img key={index} src={image} className="sweetSliderImage" />;
            })}
          </div>
        </Marquee>
      </div>
    </div>
  );
};


export default HomePage1;
